/**
 * CompareTradeIdeasModal — Obondo's "test up to 3 ideas together" tool
 * (landing-page promise: "Tests up to 3 ideas together and shows the
 * risk"). Reuses the SAME `build_pretrade_verdict` core as the single-trade
 * order-ticket review (`AiOrderReviewModal`) — this is an exploratory
 * what-if comparison, not tied to placing a real order.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Plus, Trash2, ShieldAlert, TrendingUp, TrendingDown, Scale } from "lucide-react";
import { signTone, toneClassName, toneVar } from "@/lib/numberColor";
import { fmtKES } from "@/lib/api";

const VERDICT_STYLES = {
    green: { bg: "bg-green-500/10 border-green-500/40", text: "text-green-700 dark:text-green-400", label: "Green light" },
    amber: { bg: "bg-amber-500/10 border-amber-500/40", text: "text-amber-700 dark:text-amber-400", label: "Amber · caveat" },
    red:   { bg: "bg-red-500/10 border-red-500/40",     text: "text-red-700 dark:text-red-400",     label: "Red · consider stopping" },
};

const BLANK_IDEA = { symbol: "", side: "buy", qty: "", stopLossPct: "", takeProfitPct: "" };
const MAX_IDEAS = 3;

export function CompareTradeIdeasModal({ open, onClose, accountId }) {
    const [ideas, setIdeas] = useState([{ ...BLANK_IDEA }]);
    const [state, setState] = useState("form"); // form | loading | results
    const [result, setResult] = useState(null);
    const [formError, setFormError] = useState(null);

    const updateIdea = (i, patch) => {
        setIdeas((prev) => prev.map((idea, idx) => (idx === i ? { ...idea, ...patch } : idea)));
    };
    const addIdea = () => {
        if (ideas.length >= MAX_IDEAS) return;
        setIdeas((prev) => [...prev, { ...BLANK_IDEA }]);
    };
    const removeIdea = (i) => {
        setIdeas((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));
    };

    const runCompare = async () => {
        const payload = ideas
            .filter((idea) => idea.symbol.trim() && idea.qty)
            .map((idea) => ({
                symbol: idea.symbol.trim().toUpperCase(),
                side: idea.side,
                qty: Number(idea.qty),
                account_id: accountId || undefined,
                stop_loss_pct: idea.stopLossPct ? Number(idea.stopLossPct) : undefined,
                take_profit_pct: idea.takeProfitPct ? Number(idea.takeProfitPct) : undefined,
            }));
        if (payload.length === 0) {
            setFormError("Enter at least one trade idea (symbol + quantity) to compare.");
            return;
        }
        setFormError(null);
        setState("loading");
        try {
            const { data } = await api.post("/chat/assistant/compare-trade-ideas", { ideas: payload });
            setResult(data);
            setState("results");
        } catch (e) {
            setFormError(e?.response?.data?.detail || "Obondo couldn't run this comparison — please try again.");
            setState("form");
        }
    };

    const startOver = () => {
        setResult(null);
        setState("form");
    };

    const handleClose = () => {
        onClose?.();
        setIdeas([{ ...BLANK_IDEA }]);
        setResult(null);
        setFormError(null);
        setState("form");
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
            <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="compare-trade-ideas-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Scale className="w-5 h-5 text-primary" />
                        Compare trade ideas
                    </DialogTitle>
                    <p className="text-xs text-muted-foreground">
                        Test up to 3 trade ideas together — Obondo reviews each one and shows you the risk, side by side.
                    </p>
                </DialogHeader>

                {state !== "results" && (
                    <div className="space-y-4" data-testid="compare-trade-ideas-form">
                        {ideas.map((idea, i) => (
                            <div key={i} className="border border-border rounded-lg p-3 space-y-2" data-testid={`idea-slot-${i}`}>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Idea {i + 1}</span>
                                    {ideas.length > 1 && (
                                        <button onClick={() => removeIdea(i)} className="text-muted-foreground hover:text-destructive" data-testid={`idea-remove-${i}`}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                                    <Input placeholder="Symbol (e.g. SCOM)" value={idea.symbol}
                                        onChange={(e) => updateIdea(i, { symbol: e.target.value })}
                                        className="col-span-2 sm:col-span-1" data-testid={`idea-symbol-${i}`} />
                                    <select value={idea.side} onChange={(e) => updateIdea(i, { side: e.target.value })}
                                        className="h-9 rounded-md border border-input bg-background px-2 text-sm" data-testid={`idea-side-${i}`}>
                                        <option value="buy">Buy</option>
                                        <option value="sell">Sell</option>
                                    </select>
                                    <Input placeholder="Qty" type="number" min="1" value={idea.qty}
                                        onChange={(e) => updateIdea(i, { qty: e.target.value })} data-testid={`idea-qty-${i}`} />
                                    <Input placeholder="SL %" type="number" min="0" step="0.1" value={idea.stopLossPct}
                                        onChange={(e) => updateIdea(i, { stopLossPct: e.target.value })} data-testid={`idea-sl-${i}`} />
                                    <Input placeholder="TP %" type="number" min="0" step="0.1" value={idea.takeProfitPct}
                                        onChange={(e) => updateIdea(i, { takeProfitPct: e.target.value })} data-testid={`idea-tp-${i}`} />
                                </div>
                            </div>
                        ))}

                        {ideas.length < MAX_IDEAS && (
                            <Button variant="outline" size="sm" onClick={addIdea} data-testid="idea-add-btn">
                                <Plus className="w-3.5 h-3.5 mr-1" /> Add another idea ({ideas.length}/{MAX_IDEAS})
                            </Button>
                        )}

                        {formError && (
                            <div className="text-xs text-danger flex items-center gap-1.5" data-testid="compare-form-error">
                                <ShieldAlert className="w-3.5 h-3.5" /> {formError}
                            </div>
                        )}

                        <Button onClick={runCompare} disabled={state === "loading"} className="w-full" data-testid="compare-run-btn">
                            {state === "loading" ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Obondo is comparing…</> : "Compare with Obondo"}
                        </Button>
                    </div>
                )}

                {state === "results" && result && (
                    <CompareResults result={result} onStartOver={startOver} onClose={handleClose} />
                )}
            </DialogContent>
        </Dialog>
    );
}

function CompareResults({ result, onStartOver, onClose }) {
    const { ideas, most_risk_idx, least_risk_idx } = result;
    const cols = ideas.length === 1 ? "grid-cols-1" : ideas.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-3";
    const showRanking = ideas.filter((i) => i.risk_kes != null).length > 1;

    return (
        <div className="space-y-4" data-testid="compare-trade-ideas-results">
            {showRanking && (
                <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-3" data-testid="compare-ranking-strip">
                    {most_risk_idx != null && (
                        <span className="flex items-center gap-1">
                            <TrendingDown className="w-3.5 h-3.5" style={{ color: toneVar("neg") }} />
                            Idea {most_risk_idx + 1} carries the <span className={toneClassName("neg")}>most risk</span>
                        </span>
                    )}
                    {least_risk_idx != null && least_risk_idx !== most_risk_idx && (
                        <span className="flex items-center gap-1">
                            <TrendingUp className="w-3.5 h-3.5" style={{ color: toneVar("pos") }} />
                            Idea {least_risk_idx + 1} carries the <span className={toneClassName("pos")}>least risk</span>
                        </span>
                    )}
                </div>
            )}

            <div className={`grid ${cols} gap-3`}>
                {ideas.map((idea) => (
                    <IdeaCard key={idea.idx} idea={idea}
                        isMostRisk={idea.idx === most_risk_idx && showRanking}
                        isLeastRisk={idea.idx === least_risk_idx && showRanking} />
                ))}
            </div>

            <div className="flex gap-2">
                <Button variant="outline" onClick={onStartOver} className="flex-1" data-testid="compare-start-over-btn">
                    Try different ideas
                </Button>
                <Button onClick={onClose} className="flex-1" data-testid="compare-close-btn">Done</Button>
            </div>
        </div>
    );
}

function IdeaCard({ idea, isMostRisk, isLeastRisk }) {
    const style = idea.verdict ? VERDICT_STYLES[idea.verdict] || VERDICT_STYLES.amber : null;

    return (
        <div className="border border-border rounded-lg p-3 space-y-2 relative" data-testid={`idea-result-${idea.idx}`}>
            <div className="flex items-center justify-between">
                <div className="font-bold text-sm">
                    {idea.side === "buy" ? "Buy" : "Sell"} {idea.qty} {idea.symbol}
                </div>
                {idea.is_duplicate && (
                    <span className="text-[10px] text-muted-foreground italic" data-testid={`idea-duplicate-${idea.idx}`}>duplicate</span>
                )}
            </div>

            {isMostRisk && (
                <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded" style={{ color: toneVar("neg"), backgroundColor: "hsl(var(--danger) / 0.10)" }}>
                    Most risk
                </span>
            )}
            {isLeastRisk && (
                <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded" style={{ color: toneVar("pos"), backgroundColor: "hsl(var(--success) / 0.10)" }}>
                    Least risk
                </span>
            )}

            {idea.error ? (
                <div className="text-xs text-danger flex items-start gap-1.5" data-testid={`idea-error-${idea.idx}`}>
                    <ShieldAlert className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {idea.error}
                </div>
            ) : (
                <>
                    {style && (
                        <div className={`rounded-md border p-2 ${style.bg}`} data-testid={`idea-verdict-${idea.idx}`}>
                            <div className={`text-[10px] font-bold uppercase tracking-wide mb-0.5 ${style.text}`}>{style.label}</div>
                            <div className="text-xs font-semibold">{idea.headline}</div>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                            <div className="text-muted-foreground text-[10px] uppercase">Risk (SL hit)</div>
                            <div className={`mono font-bold ${idea.risk_kes ? toneClassName("neg") : toneClassName("zero")}`} data-testid={`idea-risk-${idea.idx}`}>
                                {idea.risk_kes ? fmtKES(idea.risk_kes) : "No stop set"}
                            </div>
                        </div>
                        <div>
                            <div className="text-muted-foreground text-[10px] uppercase">Reward (TP hit)</div>
                            <div className={`mono font-bold ${idea.reward_kes ? toneClassName("pos") : toneClassName("zero")}`} data-testid={`idea-reward-${idea.idx}`}>
                                {idea.reward_kes ? fmtKES(idea.reward_kes) : "No target set"}
                            </div>
                        </div>
                    </div>

                    {idea.reward_risk_ratio != null && (
                        <div className="text-xs text-muted-foreground">Reward:risk ≈ <span className="font-semibold text-foreground">{idea.reward_risk_ratio}:1</span></div>
                    )}
                    {idea.risk_reward && <p className="text-xs leading-relaxed">{idea.risk_reward}</p>}
                    {idea.macro_note && <p className="text-xs text-muted-foreground leading-relaxed">{idea.macro_note}</p>}
                    {idea.bias_check && <p className="text-xs text-muted-foreground leading-relaxed italic">{idea.bias_check}</p>}
                    {idea.correlated_exposure && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 leading-relaxed">{idea.correlated_exposure}</p>
                    )}
                </>
            )}
        </div>
    );
}

export default CompareTradeIdeasModal;
